(ns rothko.editor.tag.transform
  (:require [com.rpl.specter :as sp]
            [rothko.editor.document.navigator :refer [selectionnav]]
            [rothko.editor.document.navigators :as n]
            [rothko.editor.document.paths :as paths]
            [rothko.editor.document.script :as script]
            [rothko.editor.space.paths :as space-paths]
            [rothko.editor.tag.paths :as tag-paths]
            [rothko.editor.word.paths :as word]
            [rothko.editor.word.transform :as word-transform]))

(defn mark-as-tagged
  ([index]
   (mark-as-tagged index index))
  ([start-index end-index]
   [:script
    (sp/srange start-index (inc end-index))
    sp/ALL
    (sp/terminal
     (fn [& args]
       (assoc (last args) :tagged? true)))]))

(def mark-as-tagged*
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (sp/transform
        (mark-as-tagged start-index end-index)
        next-fn
        structure)))]))

(def create-edit-tag*
  (selectionnav
   [tag]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (sp/transform
     ;; There are quite a few different cases to handle in this
     ;; transformation. They're quite similar so not worth commenting too
     ;; heavily.
     (let [{:keys [representation name]} tag

           selection-after-end-index
           [:selection
            (sp/multi-path
             [:start-index
              (sp/terminal-val (inc end-index))]
             [:start-offset
              (sp/terminal-val (count representation))]
             [:end-index
              (sp/terminal-val (inc end-index))]
             [:end-offset
              (sp/terminal-val (count representation))])]]
       (sp/if-path
        (word/edited? start-index)
        sp/STOP
        (sp/multi-path
         [(sp/cond-path

           ;; Selection starts on a space token ...
           (space-paths/space? start-index)
           (sp/if-path
            (space-paths/space? end-index)

            ;; ... and ends on a space token or ...
            (sp/multi-path
             (mark-as-tagged (inc start-index) (inc end-index))
             [:script
              (sp/before-index (+ 2 end-index))
              (sp/terminal-val
               {:word representation
                :name name
                :type :tag})]
             [:selection
              (sp/multi-path
               [:start-index
                (sp/terminal-val (+ 2 end-index))]
               [:start-offset
                (sp/terminal-val (count representation))]
               [:end-index
                (sp/terminal-val (+ 2 end-index))]
               [:end-offset
                (sp/terminal-val (count representation))])])

            ;; ... ends on a non-space token
            (sp/multi-path
             (mark-as-tagged (inc start-index) end-index)
             [:script
              (sp/before-index (inc end-index))
              (sp/terminal-val
               {:word representation
                :name name
                :type :tag})]
             selection-after-end-index))

           ;; A special case if we're at the end of the script
           (paths/end-of-script? start-index)
           (sp/multi-path
            (mark-as-tagged start-index end-index)
            [:script
             (sp/before-index (inc start-index))
             (sp/terminal-val
              {:word representation
               :name name
               :type :tag})]
            selection-after-end-index)

           ;; The default case that's left is if we start on a non-space
           ;; token ...
           sp/STAY
           (sp/if-path
            (space-paths/space? end-index)

            ;; ... and end on a space token or ...
            (sp/multi-path
             (mark-as-tagged start-index (dec end-index))
             [:script
              (sp/before-index end-index)
              (sp/terminal-val
               {:word representation
                :name name
                :type :tag})]
             [:selection
              (sp/multi-path
               [:start-index
                (sp/terminal-val end-index)]
               [:start-offset
                (sp/terminal-val (count representation))]
               [:end-index
                (sp/terminal-val end-index)]
               [:end-offset
                (sp/terminal-val (count representation))])])

            ;; ... end on a non-space token
            (sp/multi-path
             (mark-as-tagged start-index end-index)
             [:script
              (sp/before-index (inc end-index))
              (sp/terminal-val
               {:word representation
                :name name
                :type :tag})]
             selection-after-end-index)))])))
     next-fn
     structure))))

(defn create-edit-tag
  [tag]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (create-edit-tag* tag)]))

(def create-independent-tag*
  (selectionnav
   [tag]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (let [{:keys [representation name]} tag]
      (sp/transform
       (sp/multi-path
        [:script
         (sp/srange (inc start-index) (inc end-index))
         (sp/terminal-val
          [{:word representation
            :name name
            :type :tag}])]
        [:selection
         (sp/terminal
          (fn [& _]
            {:start-index (inc start-index)
             :start-offset (count representation)
             :end-index (inc start-index)
             :end-offset (count representation)}))])
       next-fn
       structure)))))

(defn create-independent-tag
  [tag]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (create-independent-tag* tag)]))

(def create-tag*
  (selectionnav
   [tag]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (let [{:keys [representation name]} tag]
      (sp/transform
       (sp/multi-path
        [:script
         (sp/before-index start-index)
         (sp/terminal-val
          {:word representation
           :name name
           :type :tag})]
        [:selection
         (sp/terminal
          (fn [& _]
            {:start-index start-index
             :start-offset (count representation)
             :end-index start-index
             :end-offset (count representation)}))])
       next-fn
       structure)))))

(defn create-tag
  [tag]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (create-tag* tag)]))

(def wrap-in-tokens*
  (selectionnav
   [start-token end-token]
   (transform*
    [_ {:keys [start-index end-index] :as selection} structure next-fn]
    (sp/transform
     (sp/multi-path
      [:script
       (sp/before-index start-index)
       (sp/terminal-val start-token)]
      [:script
       (sp/before-index (+ end-index 2))
       (sp/terminal-val end-token)]
      [:selection
       (sp/terminal
        (fn [& _]
          (script/caret
           (+ end-index 2)
           (-> end-token :word count))))])
     next-fn
     structure))))

(defn wrap-in-tokens
  "Wraps the selection in two tokens and moves the caret to after the end token."
  [start-token end-token]
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    (wrap-in-tokens* start-token end-token)]))

(def remove-tagged?-marking
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ {:keys [start-index]} structure next-fn]
       (sp/transform
        [:script
         (sp/srange 0 start-index)
         (sp/terminal
          (fn [structure]
            (let [[tagged-tokens rest-tokens] (split-with :tagged? (reverse structure))
                  non-tagged-tokens (map #(dissoc % :tagged?) tagged-tokens)]
              (into [] (reverse (concat non-tagged-tokens rest-tokens))))))]
        next-fn
        structure)))]))

(defn remove-at
  [{:keys [start-index start-offset end-index end-offset] :as selection}]
  (sp/multi-path

   remove-tagged?-marking

   (sp/if-path
    [:script
     (sp/nthpath start-index)
     (sp/multi-path
       (sp/must :system?)
       (sp/must :previous-stage?))]
    (sp/multi-path
     (word-transform/mark-as-edited start-index))
    [:script
     (sp/nthpath start-index)
     (sp/terminal-val sp/NONE)])

   [(sp/collect-one
     (paths/word-length
      (dec start-index)))
    :selection
    (sp/vterminal
     (fn [[prev-word-count] _]
       {:start-index (max 0 (dec start-index))
        :start-offset (or prev-word-count 0)
        :end-index (max 0 (dec start-index))
        :end-offset (or prev-word-count 0)}))]))

(def include-whole-tag
  ;; Modify offset to include the whole tag when selection includes only part of it
  (sp/transformed
   [(sp/collect-one
     n/selected)
    :selection]
   (fn [token-range {:keys [start-index start-offset end-offset end-index] :as selection}]
     (let [first-token (first token-range)
           last-token (last token-range)]
       (cond
         (and (= 1 (count token-range))
              (= :tag (:type first-token)))
         (assoc selection
                :start-offset 0
                :end-offset (-> last-token :word (count)))
         
         (and (< end-offset (-> last-token :word (count)))
              (= :tag (:type last-token)))
         (assoc selection
                :end-offset (-> last-token :word (count)))
         
         (and (< start-offset (-> first-token :word count))
              (= :tag (:type first-token)))
         (assoc selection
                :start-offset 0)
         :else selection)))))

(def remove-at-selection
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ selection structure next-fn]
       (sp/transform
        (remove-at selection)
        next-fn
        structure)))]))

(def
  ^{:doc "Removes all non-previous-stage tag tokens from the selection and
  shrinks it accordingly. Also removes `:tagged?` key from the selected tokens."}
  remove-all
  (n/remove-all
   [(complement :previous-stage?)
    tag-paths/TAG?]
   :tagged?))
